import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { login as loginApi, logout as logoutApi } from '@/api/auth';
import type { LoginRequest } from '@/api/auth';
import type { Role } from '@/lib/role';

interface AuthSnapshot {
  token: string | null;
  role: Role | null;
  username: string | null;
}

function readSnapshot(): AuthSnapshot {
  return {
    token: localStorage.getItem('token'),
    role: (localStorage.getItem('role') as Role | null) ?? null,
    username: localStorage.getItem('username'),
  };
}

/**
 * 轻量 auth hook。不引 Zustand（决策 Q2）。
 * 多组件之间靠 storage event 同步。
 */
export function useAuth() {
  const [snap, setSnap] = useState<AuthSnapshot>(readSnapshot);
  const queryClient = useQueryClient();

  useEffect(() => {
    const sync = () => setSnap(readSnapshot());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const login = useCallback(
    async (body: LoginRequest) => {
      // Sec Reviewer high#1：新登录前清掉前一会话残留缓存，防 A→B 跨会话脏读
      queryClient.clear();
      const res = await loginApi(body);
      localStorage.setItem('token', res.token);
      localStorage.setItem('role', res.role);
      localStorage.setItem('username', body.username);
      // R3-Sprint12-A UX:用后端返的 tenant_slug 覆盖 — 不再依赖前端 tenant dropdown
      // PLATFORM(tenant_slug=null)不动 localStorage(可由 Header 视角下拉切换)
      if (res.tenant_slug) {
        localStorage.setItem('tenant_code', res.tenant_slug);
        window.dispatchEvent(new Event('tenant-code-changed'));
      } else if (body.tenant_code) {
        // 兼容旧调用方:body 仍传了 tenant_code 时保留行为
        localStorage.setItem('tenant_code', body.tenant_code);
        window.dispatchEvent(new Event('tenant-code-changed'));
      }
      setSnap(readSnapshot());
      return res;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    // R3-Sprint12-B (Security):先调 BE /auth/logout 把 jti 入 Redis 黑名单
    // (网络失败 / Redis down 都 fail-open,本地清理必跑;BE 端 fail-open 已处理)
    await logoutApi();
    // Sec Reviewer high#1：logout 必须四清 — token/role/username/tenant_code + RQ 缓存全清
    // 否则 logout 后 React Query 缓存残留前会话数据，下个用户登录瞬间能看到上一用户的列表
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    localStorage.removeItem('tenant_code');
    queryClient.clear();
    setSnap(readSnapshot());
  }, [queryClient]);

  return {
    ...snap,
    isAuthenticated: !!snap.token,
    login,
    logout,
  };
}
